import { HttpClient } from '@angular/common/http';
import {
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  effect,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, retry } from 'rxjs';

interface FileMetadataResponse {
  contentUrl: string;
}

/**
 * Keeps a <video> playing from an expiring signed URL: fetches the file
 * metadata, reads the SAS expiry (`se` param) and renews the URL before any
 * range request can hit a dead token. Renewal is skipped while the whole clip
 * sits in the buffer — it loops from cache without network, so an expired
 * token is harmless (no egress, no reload blink).
 *
 * Every trigger (input change, timer at `se` - margin, `waiting`,
 * visibilitychange) goes through one gate: renewIfExpiring.
 */
@Directive({
  selector: 'video[odVideoSignedSrc]',
  standalone: true,
})
export class VideoSignedSrcDirective {
  private static readonly RENEWAL_MARGIN_S = 60;
  private static readonly RENEWAL_RETRY_COUNT = 3;
  private static readonly RENEWAL_RETRY_DELAY_MS = 2000;
  // buffered.end never lands exactly on duration.
  private static readonly BUFFER_EPSILON_S = 0.5;

  public readonly odVideoSignedSrc = input.required<string>();

  private readonly video =
    inject<ElementRef<HTMLVideoElement>>(ElementRef).nativeElement;
  private readonly httpClient = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  private expiryMs: number | null = null;
  private appliedUrl: string | null = null;
  private renewTimer: ReturnType<typeof setTimeout> | null = null;
  private fetchInFlight = false;

  constructor() {
    effect(() => {
      const metadataUrl = this.odVideoSignedSrc();
      // A session refresh re-emits the same URL (through a transient '');
      // ignore both so the playing video is not torn down and reset to 0.
      if (!metadataUrl || metadataUrl === this.appliedUrl) {
        return;
      }
      this.appliedUrl = metadataUrl;
      this.clearTimer();
      this.expiryMs = null;
      this.fetchAndApply(metadataUrl, false);
    });
    this.destroyRef.onDestroy(() => this.clearTimer());
  }

  @HostListener('document:visibilitychange')
  protected onVisibilityChange(): void {
    if (document.hidden) {
      return;
    }
    // Paused + token due after a long hide = Chromium "phantom playback"
    // (buffered/readyState/play() all report healthy but no frame advances);
    // only a src reload recovers it. autoplay-gated so a user-paused video
    // stays paused.
    if (
      this.video.autoplay &&
      this.video.paused &&
      this.isWithinRenewalMargin()
    ) {
      if (!this.fetchInFlight) {
        this.fetchAndApply(this.odVideoSignedSrc(), true);
      }
      return;
    }
    // Covers the renewal timer frozen during a system sleep.
    this.renewIfExpiring();
  }

  // Underrun with a dead token (cache eviction, wake without a visibility
  // event) → the gate renews; benign with a fresh one.
  @HostListener('waiting')
  protected onWaiting(): void {
    this.renewIfExpiring();
  }

  // Fatal media error (dead token surfaced as error, not `waiting`): reload.
  // Token-due gate keeps a genuinely broken source from retrying forever.
  @HostListener('error')
  protected onError(): void {
    if (this.fetchInFlight || !this.isWithinRenewalMargin()) {
      return;
    }
    this.fetchAndApply(this.odVideoSignedSrc(), true);
  }

  private isWithinRenewalMargin(): boolean {
    return (
      this.expiryMs !== null &&
      Date.now() >=
        this.expiryMs - VideoSignedSrcDirective.RENEWAL_MARGIN_S * 1000
    );
  }

  private renewIfExpiring(): void {
    if (this.fetchInFlight || !this.isWithinRenewalMargin()) {
      return;
    }
    if (this.isFullyBuffered()) {
      return;
    }
    this.fetchAndApply(this.odVideoSignedSrc(), true);
  }

  private fetchAndApply(metadataUrl: string, restorePlayback: boolean): void {
    this.fetchInFlight = true;
    this.httpClient
      .get<FileMetadataResponse>(metadataUrl, { withCredentials: true })
      .pipe(
        retry({
          count: VideoSignedSrcDirective.RENEWAL_RETRY_COUNT,
          delay: VideoSignedSrcDirective.RENEWAL_RETRY_DELAY_MS,
        }),
        finalize(() => (this.fetchInFlight = false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: response => {
          // Stale: input moved to another video while in flight.
          if (metadataUrl !== this.odVideoSignedSrc()) {
            return;
          }
          this.appliedUrl = metadataUrl;
          this.applySrc(response.contentUrl, restorePlayback);
          this.expiryMs = this.getExpiryMs(response.contentUrl);
          this.scheduleRenewal();
        },
        error: () => {
          if (metadataUrl !== this.odVideoSignedSrc()) {
            return;
          }
          // A failed initial load leaves no src; clearing appliedUrl lets the
          // session refresh's re-emission act as the retry.
          if (!restorePlayback) {
            this.appliedUrl = null;
          }
        },
      });
  }

  private applySrc(url: string, restorePlayback: boolean): void {
    if (!restorePlayback) {
      this.video.src = url;
      return;
    }

    // Captured before load(), which resets currentTime to 0 and rate to 1.
    const resumeAt = this.video.currentTime;
    const resumeRate = this.video.playbackRate;
    const shouldPlay = !this.video.paused || this.video.autoplay;
    this.video.addEventListener(
      'loadeddata',
      () => {
        this.video.playbackRate = resumeRate;
        if (resumeAt > 0 && resumeAt < this.video.duration) {
          this.video.currentTime = resumeAt;
        }
        if (shouldPlay) {
          // Rejected in hidden tabs (power saving); browser resumes on return.
          this.video.play().catch(() => undefined);
        }
      },
      { once: true }
    );
    this.video.src = url;
    this.video.load();
  }

  private getExpiryMs(contentUrl: string): number | null {
    const se = new URL(contentUrl, document.baseURI).searchParams.get('se');
    const expiryMs = se ? Date.parse(se) : NaN;
    return Number.isNaN(expiryMs) ? null : expiryMs;
  }

  // [0, duration] covered across (possibly fragmented) ranges = loops without
  // further requests. Firefox may report 0 ranges for large videos → renews.
  private isFullyBuffered(): boolean {
    const { buffered, duration } = this.video;
    if (!duration) {
      return false;
    }
    const epsilon = VideoSignedSrcDirective.BUFFER_EPSILON_S;
    let covered = 0;
    for (let i = 0; i < buffered.length; i++) {
      if (buffered.start(i) - covered > epsilon) {
        return false;
      }
      covered = buffered.end(i);
    }
    return duration - covered <= epsilon;
  }

  private scheduleRenewal(): void {
    this.clearTimer();
    if (this.expiryMs === null) {
      return;
    }
    const delayMs =
      this.expiryMs -
      VideoSignedSrcDirective.RENEWAL_MARGIN_S * 1000 -
      Date.now();
    this.renewTimer = setTimeout(() => {
      this.renewTimer = null;
      this.renewIfExpiring();
    }, delayMs);
  }

  private clearTimer(): void {
    if (this.renewTimer !== null) {
      clearTimeout(this.renewTimer);
      this.renewTimer = null;
    }
  }
}
